'use strict';

define('vb/private/stateManagement/router/vbRootRouter',[
  'vb/private/stateManagement/router/vbRouter',
  'vb/private/stateManagement/router/urlPathAdapter',
  'vb/private/history',
  'vb/private/log',
], (VbRouter, UrlPathAdapter, VbHistory, Log) => {
  const log = Log.getLogger('/vb/stateManagement/router/vbRootRouter');

  /**
   * @typedef {import('vb/private/stateManagement/router/urlParamAdapter').default} UrlParamAdapter
   * @typedef {import('vb/private/stateManagement/router/historyAdapter').default} HistoryAdapter
   * @typedef {import('vb/private/stateManagement/page').default} Page
   */

  /**
   * The default name for an instance of a root router
   */
  const DEFAULT_ROOT_NAME = 'root';

  /**
   * Used for a router at the top of the router hierarchy. This is not necessarily the first router.
   * When in a switcher, the top of the hierarchy is the first router in a switcher element.
   * A root router is responsible of the router state management using the browser history.
   */
  class VbRootRouter extends VbRouter {
    /**
     * @param {string} [key=VbRootRouter.DEFAULT_ROOT_NAME]
     * @param {UrlPathAdapter|UrlParamAdapter|HistoryAdapter} [urlAdapter=UrlPathAdapter]
     * Default adapter is the UrlPathAdapter
     */
    constructor(key = DEFAULT_ROOT_NAME, urlAdapter = new UrlPathAdapter()) {
      super(key);

      /**
       * @type {typeof VbHistory}
       */
      this.history = VbHistory;

      /**
       * Hold the url adapter to be used.
       * @type {UrlPathAdapter|UrlParamAdapter|HistoryAdapter}
       */
      this.urlAdapter = urlAdapter;

      /**
       * @private
       * @type {string|null}
       */
      this._currentNavPath = null;

      this.setTopRootInstance();
    }

    /**
     * Set this root router as the top level root router
     * Needed so the SwitcherRootRouter can override this function
     */
    setTopRootInstance() {
      VbRouter.updateRootInstance(this);
    }

    /**
     * Override VbRouter implementation
     * When the recursive call going up the router hierarch reaches this point, it is a root router.
     * @returns {VbRootRouter}
     */
    getRootRouter() {
      return this;
    }

    /**
     * Sets the current navigation path for this router.
     * It is set by the application when handling the navigated event.
     * This is used when navigating to recognize when navigating to the same page
     *
     * @param {String}  navPath  The navigation path
     */
    setCurrentNavPath(navPath) {
      this._currentNavPath = navPath;
    }

    /**
     * Gets the current navigation path.
     * This is used when navigating to recognize when navigating to the same page
     *
     * @return {String|null}  The current navigation path.
     */
    getCurrentNavPath() {
      return this._currentNavPath;
    }

    /**
     * Called by the History class when the popstate listener is invoked. See History.init() and Router.init()
     * @param {{direction: {string}, change: {number}}} details - Additional info about the
     * popState event(steps and direction)
     */
    handlePopState(details) {
      log.finer('Handling popState event with URL:', window.location.href);

      VbRouter.queueTransition({
        rootRouter: this,
        router: this,
        origin: 'popState',
        details,
      });
    }

    /**
     * @param {Array<Object>} newStates
     * @param {boolean} replace
     */
    updateUrl(newStates, replace) {
      const url = this.urlAdapter.buildUrlFromStates(newStates);
      if (!replace) {
        this.history.pushState();
      }

      this.history.setUrl(url);
      this.history.sync();
    }

    /**
     * Updates the application state by pushing a new state to the browser history.
     *
     * @param {string|null} pagePath - The new page path to set in the browser's history.
     * @param {boolean} [replace=false] - Whether to replace the current state in the browser's history
     * instead of pushing a new one.
     * @returns {Promise<void>} A promise that resolves when the state has been updated.
     */
    updateState(pagePath, replace) {
      return Promise.resolve().then(() => {
        // Push the new state to the browser history
        if (!replace) {
          this.history.pushState();
        }

        // pagePath can be null when called from SwitcherRootMixin.updateUrl()
        // It means to not change the current pagePath in history
        if (pagePath !== null) {
          this.history.setPagePath(pagePath);
        }

        return this.history.sync();
      });
    }

    /**
     * Implementation of the navigate back action.
     * @param {Page} page
     * @param {Object} [params]
     * @returns {Promise<void>}
     */
    navigateBack(page, params) {
      return Promise.resolve().then(() => {
        this.history.backAction(params);
        return undefined;
      });
    }

    dispose() {
      this._currentNavPath = null;
      super.dispose();
    }
  }

  return VbRootRouter;
});

